/** Shared constants for the tenant theming layer. */

/**
 * localStorage namespace for the cached tab identity. The hostname is appended,
 * so one workspace's tab never carries another's name or icon.
 */
export const TENANT_HEAD_CACHE_PREFIX = 'minetech.head.';

/** Matches the <link rel="icon"> in index.html. */
export const APP_FAVICON = '/favicon.svg';

/**
 * The `type` attribute to put on a `<link rel="icon">`, chosen by file
 * extension. Browsers pick between several declared icons using this hint, and
 * an SVG served without it is skipped by some of them.
 */
export const FAVICON_MIME_BY_EXTENSION: Record<string, string> = {
  svg: 'image/svg+xml',
  png: 'image/png',
  ico: 'image/x-icon',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

/**
 * localStorage namespace for the last resolved workspace. Also hostname-keyed,
 * so one tenant's cached identity is never shown on another tenant's origin.
 */
export const TENANT_HOST_CACHE_PREFIX = 'minetech.tenant-host.';

export const DEFAULT_PRIMARY = '#7C2A13';
export const DEFAULT_ACCENT = '#F24A02';

export const DEFAULT_FONT_FAMILY = 'SYSTEM';

export const TENANT_FONT_CACHE_PREFIX = 'minetech.font.';

export const TENANT_FONT_STYLE_ID = 'tenant-font';
export const TENANT_FONT_LINK_ID = 'tenant-font-webfont';

interface FontStack {

  stack: string;
  googleFamily?: string;
}

/** Mirrors ETenantFontFamily on the backend. */
export const FONT_STACK_BY_FAMILY: Record<string, FontStack> = {
  SYSTEM: {
    stack: "'Host Grotesk', system-ui, -apple-system, sans-serif",
  },
  INTER: {
    stack: "'Inter', 'Host Grotesk', system-ui, sans-serif",
    googleFamily: 'Inter',
  },
  ROBOTO: {
    stack: "'Roboto', 'Host Grotesk', system-ui, sans-serif",
    googleFamily: 'Roboto',
  },
  OPEN_SANS: {
    stack: "'Open Sans', 'Host Grotesk', system-ui, sans-serif",
    googleFamily: 'Open Sans',
  },
  LATO: {
    stack: "'Lato', 'Host Grotesk', system-ui, sans-serif",
    googleFamily: 'Lato',
  },
  SOURCE_SANS: {
    stack: "'Source Sans 3', 'Host Grotesk', system-ui, sans-serif",
    googleFamily: 'Source Sans 3',
  },
};

export const FONT_FAMILY_OPTIONS = [
  { value: 'SYSTEM', label: 'System default (Host Grotesk)' },
  { value: 'INTER', label: 'Inter' },
  { value: 'ROBOTO', label: 'Roboto' },
  { value: 'OPEN_SANS', label: 'Open Sans' },
  { value: 'LATO', label: 'Lato' },
  { value: 'SOURCE_SANS', label: 'Source Sans 3' },
] as const;

/**
 * The imagery beside the sign-in form when a workspace has not chosen its own.
 * Mirrors DEFAULT_HERO_IMAGES in the backend registry.
 */
export const DEFAULT_HERO_IMAGES = [
  '/images/auth/mining-1.jpg',
  '/images/auth/mining-2.jpg',
  '/images/auth/mining-3.jpg',
  '/images/auth/mining-4.jpg',
];

/** Matches assetReferenceList's cap in the backend registry. */
export const MAX_HERO_IMAGES = 4;

/** Seconds each image holds before the next fades in. */
export const HERO_SLOT_SECONDS = 6;

export const HERO_CAROUSEL_NAME = 'heroFade';
